interface IBackendResponse<T> {
	status: number;
	data: T | null;
	violations?: { message?: string }[];
}

/**
 * Generic utility function to fetch data from an API endpoint with optional headers and request options.
 * This function supports attaching a user token from `localStorage` for authorization,
 * as well as custom headers and error handling.
 */
export const fetchData = async <T>(
	url: string,
	options: RequestInit = {},
	config?: { authRoute?: boolean }
): Promise<IBackendResponse<T>> => {
	const token = window.localStorage.getItem('userToken');

	const defaultHeaders: Record<string, string> = {
		'Content-Type': 'application/json',
		Accept: 'application/json',
	};

	const headers: Record<string, string> = {
		...defaultHeaders,
		...(options.headers as Record<string, string> | undefined),
	};

	if (token && config?.authRoute) {
		headers['Authorization'] = `${token}`;
	}
	if (options.method === 'PATCH') {
		headers['Content-Type'] = 'application/merge-patch+json';
	}

	try {
		const response = await fetch(`${import.meta.env.VITE_API_URL}` + url, {
			...options,
			headers,
		});

		const status = response.status;

		// Handle non-success status codes
		if (status >= 300) {
			// Attempt to parse the error message from the response if possible
			const responseData = await response.json().catch(() => null);
			return {
				status,
				data: responseData,
				violations: responseData?.violations,
			};
		}

		// Handle no content (status 204) or empty response body
		if (status === 204 || response.headers.get('Content-Length') === '0') {
			return { status, data: null };
		}

		// Parse and return the response body
		const responseData = await response.json();
		return { status, data: responseData as T };
	} catch (error) {
		console.error('Fetch Error:', error);
		throw error;
	}
};
